/**
 * Universal EventBus with Lazy Script Loading
 * 
 * Creates a global EventBus that can be used on any website.
 * Automatically detects user interactions and emits 'page:interaction' events.
 * Perfect for lazy loading scripts on user interaction.
 */

(function() {
    'use strict';

    /**
     * Simple EventBus Implementation
     */
    class EventBus {
        constructor() {
            this.bus = {};
            this.hasInteracted = false;
            this.interactionEvents = [
                'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart',
                'click', 'touchend', 'mouseup', 'focus', 'blur', 'resize',
                'orientationchange', 'visibilitychange'
            ];
            this.pageChangeEvents = [
                'popstate', 'pushstate', 'replacestate', 'hashchange',
                'beforeunload', 'unload', 'load', 'DOMContentLoaded'
            ];
            this.debug = false; // Set to true to see debug logs
            
            this.init();
        }

        /**
         * Subscribe to events
         * @param {string|Array} eventType - Event name or array of event names
         * @param {Function} callback - Callback function
         */
        on(eventType, callback) {
            if (Array.isArray(eventType)) {
                for (let i = 0; i < eventType.length; i++) {
                    this.on(eventType[i], callback);
                }
            } else {
                if (!this.bus.hasOwnProperty(eventType)) {
                    this.bus[eventType] = [];
                }
                this.bus[eventType].push(callback);
                this.log(`Subscribed to event: ${eventType}`);
            }
        }

        /**
         * Emit events
         * @param {string} eventType - Event name
         * @param {*} data - Optional data to pass to callbacks
         */
        emit(eventType, data = null) {
            if (this.bus.hasOwnProperty(eventType)) {
                this.log(`Emitting event: ${eventType}`, data);
                for (const callback of this.bus[eventType]) {
                    try {
                        callback(data);
                    } catch (error) {
                        console.error(`Error in EventBus callback for ${eventType}:`, error);
                    }
                }
            }
        }

        /**
         * Remove all listeners for an event type
         * @param {string} eventType - Event name
         */
        remove(eventType) {
            delete this.bus[eventType];
            this.log(`Removed all listeners for: ${eventType}`);
        }

        /**
         * Remove a specific callback for an event type
         * @param {string} eventType - Event name
         * @param {Function} callback - Specific callback to remove
         */
        off(eventType, callback) {
            if (this.bus[eventType]) {
                const index = this.bus[eventType].indexOf(callback);
                if (index > -1) {
                    this.bus[eventType].splice(index, 1);
                    this.log(`Removed specific listener for: ${eventType}`);
                }
            }
        }

        /**
         * Initialize interaction detection
         */
        init() {
            this.log('Initializing Universal EventBus');
            this.setupInteractionListeners();
            this.setupPageChangeListeners();
            this.setupHistoryAPIListeners();
            this.log('EventBus ready - listening for interactions and page changes');
        }

        /**
         * Setup interaction event listeners
         */
        setupInteractionListeners() {
            const onInteraction = (event) => {
                if (!this.hasInteracted) {
                    this.hasInteracted = true;
                    this.log('First user interaction detected:', event.type);
                    
                    // Emit the page:interaction event
                    this.emit('page:interaction', {
                        type: event.type,
                        timestamp: Date.now()
                    });
                    
                    // Remove listeners after first interaction (optional - can be changed)
                    this.cleanupInteractionListeners();
                }
            };

            // Store reference for cleanup
            this.interactionHandler = onInteraction;

            // Add listeners for all interaction types
            this.interactionEvents.forEach(eventType => {
                document.addEventListener(eventType, onInteraction, true);
            });

            this.log(`Setup interaction listeners for: ${this.interactionEvents.join(', ')}`);
        }

        /**
         * Setup page change event listeners
         */
        setupPageChangeListeners() {
            const onPageChange = (event) => {
                this.log('Page change detected:', event.type);
                this.emit('page:change', {
                    type: event.type,
                    timestamp: Date.now(),
                    url: window.location.href
                });
            };

            // Store reference for cleanup
            this.pageChangeHandler = onPageChange;

            // Add listeners for page change events
            this.pageChangeEvents.forEach(eventType => {
                if (eventType === 'pushstate' || eventType === 'replacestate') {
                    // These are not real events, we'll handle them via History API
                    return;
                }
                window.addEventListener(eventType, onPageChange, true);
            });

            this.log(`Setup page change listeners for: ${this.pageChangeEvents.filter(e => e !== 'pushstate' && e !== 'replacestate').join(', ')}`);
        }

        /**
         * Setup History API listeners for SPA navigation
         */
        setupHistoryAPIListeners() {
            // Override pushState and replaceState to detect programmatic navigation
            const originalPushState = history.pushState;
            const originalReplaceState = history.replaceState;

            history.pushState = function(...args) {
                originalPushState.apply(history, args);
                if (window.EventBus) {
                    window.EventBus.emit('page:change', {
                        type: 'pushstate',
                        timestamp: Date.now(),
                        url: window.location.href
                    });
                }
            };

            history.replaceState = function(...args) {
                originalReplaceState.apply(history, args);
                if (window.EventBus) {
                    window.EventBus.emit('page:change', {
                        type: 'replacestate',
                        timestamp: Date.now(),
                        url: window.location.href
                    });
                }
            };

            this.log('Setup History API listeners for SPA navigation');
        }

        /**
         * Cleanup interaction listeners (called after first interaction)
         */
        cleanupInteractionListeners() {
            if (this.interactionHandler) {
                this.interactionEvents.forEach(eventType => {
                    document.removeEventListener(eventType, this.interactionHandler, true);
                });
                this.log('Cleaned up interaction listeners');
            }
        }

        /**
         * Reset interaction state (useful for testing)
         */
        reset() {
            this.hasInteracted = false;
            this.bus = {};
            this.setupInteractionListeners();
            this.log('EventBus reset');
        }

        /**
         * Get current state
         */
        getState() {
            return {
                hasInteracted: this.hasInteracted,
                eventTypes: Object.keys(this.bus),
                listenerCounts: Object.keys(this.bus).reduce((acc, key) => {
                    acc[key] = this.bus[key].length;
                    return acc;
                }, {})
            };
        }

        /**
         * Enable/disable debug logging
         */
        setDebug(enabled) {
            this.debug = enabled;
            this.log(`Debug ${enabled ? 'enabled' : 'disabled'}`);
        }

        /**
         * Debug logging
         */
        log(message, data = null) {
            if (this.debug) {
                if (data) {
                    console.log(`[EventBus] ${message}`, data);
                } else {
                    console.log(`[EventBus] ${message}`);
                }
            }
        }
    }

    /**
     * Initialize the EventBus
     */
    function initializeEventBus() {
        // Check if EventBus already exists
        if (window.EventBus) {
            return;
        }

        // Create and expose the EventBus globally
        window.EventBus = new EventBus();

        // Expose utility methods
        window.EventBusUtils = {
            // Helper to create lazy script loaders
            createScriptLoader: function(src, options = {}) {
                const {
                    name = src,
                    async = true,
                    defer = false,
                    onLoad = null,
                    onError = null
                } = options;

                return function loadScript() {
                    // Prevent duplicate loading
                    if (document.querySelector(`script[src="${src}"]`)) {
                        return;
                    }

                    const script = document.createElement('script');
                    script.src = src;
                    script.async = async;
                    script.defer = defer;

                    if (onLoad) script.onload = onLoad;
                    if (onError) script.onerror = onError;

                    document.head.appendChild(script);
                };
            },

            // Helper to get EventBus state
            getState: function() {
                return window.EventBus ? window.EventBus.getState() : null;
            },

            // Helper to enable debug mode
            enableDebug: function() {
                if (window.EventBus) {
                    window.EventBus.setDebug(true);
                }
            }
        };
    }

    /**
     * Initialize when DOM is ready
     */
    function safeInitialize() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initializeEventBus);
        } else {
            initializeEventBus();
        }
    }

    // Initialize immediately
    if (typeof window !== 'undefined') {
        safeInitialize();
    }

    // Also expose the class for advanced users
    window.EventBusClass = EventBus;

})();

// Example usage (can be removed in production):
/*
document.addEventListener("DOMContentLoaded", function() {
    // Wait for EventBus to be available
    if (window.EventBus) {
        function loadAppLovinScript() {
            if (document.querySelector('script[src*="res4.applovin.com"]')) return;
            
            const script = document.createElement("script");
            script.src = "https://res4.applovin.com/p/l/loader.iife.js";
            script.async = true;
            document.head.appendChild(script);
            console.log('AppLovin script loading...');
        }
        
        window.EventBus.on("page:interaction", loadAppLovinScript);
        console.log('AppLovin script loader registered');
    }
});
*/
